import { h } from 'preact';
import UIElement from '../../internal/UIElement/UIElement';
import IssuerList from '../../internal/IssuerList';
import getIssuerImageUrl from '../../../utils/get-issuer-image';
import { FALLBACK_CONTEXT } from '../../../core/config';
import RedirectButton from '../../internal/RedirectButton';
import { IssuerListConfiguration, IssuerListData } from './types';
import type { ICore } from '../../../core/types';
import { PaymentData, PaymentMethodBrand } from '../../../types/global-types';
import { ImageOptions } from '../../../core/Context/Resources';

class IssuerListContainer<
    TProps extends IssuerListConfiguration = IssuerListConfiguration,
    TData extends PaymentData = IssuerListData
> extends UIElement<TProps> {
    protected static readonly defaultProps = {
        showImage: true,
        issuers: [],
        highlightedIssuers: [],
        loadingContext: FALLBACK_CONTEXT,
        showPaymentMethodItemImages: false
    };

    constructor(checkout: ICore, props?: TProps) {
        super(checkout, props);

        const getImage = (props: ImageOptions) => this.resources.getImage(props);

        if (this.props.showImage) {
            const getIssuerIcon = getIssuerImageUrl({ loadingContext: this.props.loadingContext }, this.constructor['type'], getImage);

            this.props.issuers = this.props.issuers.map(item => ({
                ...item,
                icon: getIssuerIcon(item.id)
            }));
        }
    }

    formatProps(props: TProps): TProps {
        const issuers =
            // @ts-expect-error - details is not in the props type but we need it for backward compatibility
            (props?.details?.length && props.details.find((d: Record<string, unknown>) => d.key === 'issuer')?.items) || props.issuers || [];
        return { ...props, issuers };
    }

    /**
     * Formats the component data output
     */
    formatData(): TData {
        return {
            paymentMethod: {
                type: this.type,
                issuer: this.state?.data?.issuer
            }
        } as unknown as TData;
    }

    /**
     * Returns whether the component state is valid or not
     */
    get isValid() {
        if (this.props.issuers.length === 0) {
            return true;
        }
        return Boolean(this.state?.isValid);
    }

    /**
     * Returns brands array (similar to card) depending on showPaymentMethodItemImages
     * This is used to show the brands in the PaymentMethodItem
     * Requires brands icons to be loaded in the payment method
     */
    get brands(): PaymentMethodBrand[] {
        if (this.props.showPaymentMethodItemImages) {
            return this.props.issuers.map(brand => {
                const brandIcon = brand.icon;
                return { icon: brandIcon, name: brand.id };
            });
        }

        return [];
    }

    protected override componentToRender(): h.JSX.Element {
        return this.props.issuers.length > 0 ? (
            <IssuerList
                items={this.props.issuers}
                highlightedIds={this.props.highlightedIssuers}
                {...this.props}
                {...this.state}
                setComponentRef={this.setComponentRef}
                showImage={this.props.showImage}
                type={this.constructor['type']}
                onChange={this.setState}
                onSubmit={this.submit}
                payButton={this.payButton}
                contextualText={this.props.i18n.get('issuerList.selectField.contextualText')}
                onSubmitAnalytics={this.submitAnalytics}
            />
        ) : (
            <RedirectButton
                name={this.props.name}
                {...this.props}
                onSubmit={this.submit}
                payButton={this.payButton}
                setComponentRef={this.setComponentRef}
            />
        );
    }
}

export default IssuerListContainer;
